import {
  ceilingYAt,
  floorYAt,
  WAVES_PERFECT_CENTER_BAND_FRAC,
  type WavesGate,
  type WavesObstacle,
  type WavesSaw,
  type WavesTerrainWall,
} from '../../wavesChart.js';
import { GOOD_SCORE, PERFECT_SCORE } from '../../gameTypes.js';
import { comboMultiplier, computeAccuracy } from '../../scoring.js';
import type {
  WavesEngineConfig,
  WavesEngineState,
  WavesPassVerdict,
  WavesResolution,
} from './state.js';

export type WavesEngineInputType = 'down' | 'up';

/**
 * One captured Waves input — a discrete edge event (press or release).
 * Identical lexicon to mania (`down`/`up`); we ship one boolean of state
 * to flip on edge, no lane index.
 */
export interface WavesEngineInput {
  beat: number;
  type: WavesEngineInputType;
}

/**
 * Events the engine emits. Wired to renderer FX, audio SFX, and the
 * shared `LoopRunState` star/cup tracker.
 *
 * - `edge`: input edge (down or up) — used for SFX and trail visuals.
 * - `obstacle_passed`: player crossed an obstacle alive — used for
 *   floating score, particle bursts, and shared milestone progress.
 * - `death`: player collided — used for screen-shake, fail FX,
 *   audio cut. Carries the obstacle id.
 * - `completed`: a full loop cleared without dying.
 */
export type WavesEngineEvent =
  | { type: 'edge'; beat: number; payload: { pressing: boolean; playerY: number } }
  | {
      type: 'obstacle_passed';
      beat: number;
      payload: {
        obstacleId: string;
        scoreDelta: number;
        combo: number;
        verdict: WavesPassVerdict;
      };
    }
  | { type: 'death'; beat: number; payload: { obstacleId: string; reason: 'collided' } }
  | { type: 'completed'; beat: number };

export interface WavesStepInput {
  config: WavesEngineConfig;
  state: WavesEngineState;
  fromBeat: number;
  toBeat: number;
  inputs: WavesEngineInput[];
}

export interface WavesStepOutput {
  state: WavesEngineState;
  events: WavesEngineEvent[];
}

/**
 * Compute the player's Y position at a target beat given the current
 * piecewise-linear segment anchored at `state.lastEdgeBeat`. Does NOT
 * mutate state — callers use this to peek at the trajectory at an
 * obstacle's beat without committing to it.
 *
 * `direction` is encoded directly in the formula:
 *   pressing → -1 (toward the ceiling, smaller Y)
 *   released → +1 (toward the floor, larger Y)
 *
 * The diagonal is then clamped to the terrain corridor at `targetBeat`
 * — the ship GLIDES along the floor/ceiling rather than passing
 * through. Because every ramp slope is `<= vYPerBeat`, the ship can
 * never separate from a CONTINUOUS surface faster than it moves, so over
 * ramps this clamp is a pure closed-form function of `(lastEdgeBeat,
 * lastEdgeY, pressing, terrain, beat)`: re-anchoring at any tick boundary
 * reproduces the same trajectory.
 *
 * The one place a single endpoint clamp is NOT enough is a lethal
 * vertical wall, where the corridor shifts discontinuously. Sampling
 * only at `targetBeat` (the post-wall corridor) would let a step window
 * straddling the wall extrapolate a free fall from the pre-wall anchor
 * and tunnel the ship through the surface it was gliding on. So
 * `evaluateHazardsInRange` re-anchors (commits the trajectory) at every
 * cleared wall beat — once that's in place, client (per-frame) and
 * server (1/16-beat) replays are bit-identical regardless of where their
 * tick boundaries fall.
 *
 * This is the only place the +/− Y convention lives — everything else
 * goes through this helper so a future inversion (e.g. flipping the
 * camera) only changes one line.
 */
export function computePlayerYAt(
  state: WavesEngineState,
  config: WavesEngineConfig,
  targetBeat: number,
): number {
  if (targetBeat <= state.lastEdgeBeat) {
    return clampPlayerY(state.lastEdgeY, config, state.lastEdgeBeat);
  }
  const dtBeats = targetBeat - state.lastEdgeBeat;
  const sign = state.pressing ? -1 : 1;
  const rawY = state.lastEdgeY + sign * config.vYPerBeat * dtBeats;
  return clampPlayerY(rawY, config, targetBeat);
}

/**
 * Clamp `y` to the playable corridor at `beat` — left-continuous at
 * walls (a wall's pre-step surface), so a low-gliding ship sits on the
 * lower floor and the wall's lethal check decides the impact. The
 * validator guarantees the corridor never pinches shut, but if a
 * degenerate chart slipped through we centre rather than invert.
 */
function clampPlayerY(y: number, config: WavesEngineConfig, beat: number): number {
  const minY = ceilingYAt(config.terrain, beat) + config.playerRadius;
  const maxY = floorYAt(config.terrain, beat) - config.playerRadius;
  if (minY >= maxY) {
    return (minY + maxY) / 2;
  }
  return Math.max(minY, Math.min(maxY, y));
}

/** Minimum clearance from a saw edge (playfield units) for a perfect pass. */
const WAVES_SAW_PERFECT_CLEARANCE = 28;

export function gradeObstaclePass(
  obs: WavesObstacle,
  playerY: number,
  playerRadius: number,
): WavesPassVerdict {
  if (obs.kind === 'gate') {
    const gapCenter = (obs.gapTopY + obs.gapBottomY) / 2;
    const halfPlayable = (obs.gapBottomY - obs.gapTopY) / 2 - playerRadius;
    if (halfPlayable <= 0) {
      return 'perfect';
    }
    const distFromCenter = Math.abs(playerY - gapCenter);
    return distFromCenter <= halfPlayable * WAVES_PERFECT_CENTER_BAND_FRAC
      ? 'perfect'
      : 'good';
  }
  const edgeClearance = Math.abs(playerY - obs.centerY) - obs.radius - playerRadius;
  return edgeClearance >= WAVES_SAW_PERFECT_CLEARANCE ? 'perfect' : 'good';
}

/**
 * Return true iff the player's bounding circle at `playerY` collides
 * with `gate`. A gate is a vertical wall with a safe gap between
 * `gapTopY` and `gapBottomY`; the player's circle (radius
 * `playerRadius`) must fully sit inside the gap to clear.
 */
function gateCollides(gate: WavesGate, playerY: number, playerRadius: number): boolean {
  // Player's circle extends [playerY - r, playerY + r]; for safe
  // passage the whole interval must sit inside [gapTopY, gapBottomY].
  // Any part poking out kills.
  return (
    playerY - playerRadius < gate.gapTopY ||
    playerY + playerRadius > gate.gapBottomY
  );
}

/**
 * Return true iff the player's bounding circle at `playerY` overlaps
 * the saw's body. Saws live at a fixed `(beat, centerY)` and have a
 * full circular hitbox; the check is the standard sum-of-radii test.
 */
function sawCollides(saw: WavesSaw, playerY: number, playerRadius: number): boolean {
  const dy = playerY - saw.centerY;
  const total = saw.radius + playerRadius;
  return dy * dy < total * total;
}

/**
 * Return true iff the ship's bounding circle overlaps a lethal vertical
 * wall at its beat — i.e. the ship failed to clear the 90-degree face
 * in time. The same vertical-extent overlap test serves both an upward
 * floor step and a downward ceiling step (the wall is normalised to
 * `top < bottom`).
 */
function wallCollides(wall: WavesTerrainWall, playerY: number, playerRadius: number): boolean {
  return playerY + playerRadius > wall.top && playerY - playerRadius < wall.bottom;
}

function obstacleCollides(
  obs: WavesObstacle,
  playerY: number,
  playerRadius: number,
): boolean {
  if (obs.kind === 'gate') {
    return gateCollides(obs, playerY, playerRadius);
  }
  return sawCollides(obs, playerY, playerRadius);
}

/**
 * Clearance (vertical units to the nearest fatal edge). Useful for
 * close-call score bonuses later — currently surfaced on
 * {@link WavesResolution.clearanceY} for analytics + a possible
 * Slice 9 close-call bonus.
 */
function clearanceFor(
  obs: WavesObstacle,
  playerY: number,
): number {
  if (obs.kind === 'gate') {
    return Math.min(playerY - obs.gapTopY, obs.gapBottomY - playerY);
  }
  return Math.abs(playerY - obs.centerY) - obs.radius;
}

/**
 * End the run on a collision. Commits the trajectory at the collision
 * beat so death FX spawn at the real impact Y and any same-tick
 * milestones still bank. `hazardId` is the obstacle or wall id (walls
 * use a synthetic `wall-NNNN` id) for death attribution.
 */
function recordDeath(
  state: WavesEngineState,
  config: WavesEngineConfig,
  beat: number,
  hazardId: string,
  events: WavesEngineEvent[],
): void {
  advancePlayerTo(state, config, beat);
  state.beat = beat;
  state.alive = false;
  state.deathReason = 'collided';
  state.deathBeat = beat;
  state.collisionObstacleId = hazardId;
  state.combo = 0;
  events.push({ type: 'death', beat, payload: { obstacleId: hazardId, reason: 'collided' } });
}

/**
 * Advance the engine through every hazard whose beat lies in
 * `[fromBeat, toBeat]` — obstacles (scored) and lethal terrain walls
 * (pass/fail) merged in beat order so death always lands on the
 * earliest collision. At each hazard, sample the player's trajectory
 * at its beat and either pass it or end the run. Obstacles and walls
 * are each pre-sorted by beat ascending.
 *
 * Mutates state in place. Pushes events for the caller to fan out.
 */
function evaluateHazardsInRange(
  state: WavesEngineState,
  config: WavesEngineConfig,
  fromBeat: number,
  toBeat: number,
  events: WavesEngineEvent[],
): void {
  const obstacles = config.chart.obstacles;
  const walls = config.walls;
  let oi = 0;
  let wi = 0;
  for (;;) {
    while (
      oi < obstacles.length &&
      (state.passedObstacleIds.has(obstacles[oi].id) || obstacles[oi].beat < fromBeat)
    ) {
      oi += 1;
    }
    while (
      wi < walls.length &&
      (state.passedWallIds.has(walls[wi].id) || walls[wi].beat < fromBeat)
    ) {
      wi += 1;
    }
    const obs = oi < obstacles.length && obstacles[oi].beat <= toBeat ? obstacles[oi] : null;
    const wall = wi < walls.length && walls[wi].beat <= toBeat ? walls[wi] : null;
    if (obs === null && wall === null) {
      return;
    }
    // Earliest-beat hazard first; obstacles win exact ties (a co-located
    // wall + obstacle is the rare degenerate case the generator avoids).
    if (wall !== null && (obs === null || wall.beat < obs.beat)) {
      const playerY = computePlayerYAt(state, config, wall.beat);
      if (wallCollides(wall, playerY, config.playerRadius)) {
        recordDeath(state, config, wall.beat, wall.id, events);
        return;
      }
      // Commit the trajectory to the wall beat, clamped to the PRE-wall
      // corridor (`computePlayerYAt` reads the left-continuous surface).
      // A wall is a discontinuous corridor shift, and the glide-clamp only
      // samples the corridor at a single target beat — so a step window
      // that straddles the wall would otherwise extrapolate the post-wall
      // position as a free trajectory from the pre-wall anchor, letting the
      // ship "tunnel" through the surface it was gliding on. Re-anchoring
      // here keeps the trajectory identical no matter where the caller's
      // tick boundaries fall (client per-frame rAF vs server 1/16-beat),
      // which is what the replay-determinism guarantee relies on.
      //
      // INVARIANT: re-anchoring only at LETHAL walls is sufficient because
      // both terrain generators shift the whole corridor at once (floor
      // and ceiling by the same delta), so every discontinuity is one
      // lethal wall plus a co-located non-lethal step that this same
      // commit absorbs. A generator that emitted a lone non-lethal step
      // (one surface jumping while the other stays put) would reintroduce
      // the straddle-tunnel divergence and need a re-anchor here too.
      advancePlayerTo(state, config, wall.beat);
      state.passedWallIds.add(wall.id);
      wi += 1;
      continue;
    }
    if (obs === null) {
      // Unreachable: a null obs here implies wall was non-null and would
      // have been taken above. Narrows the type for the obstacle branch.
      return;
    }
    const playerYAtObstacle = computePlayerYAt(state, config, obs.beat);
    if (obstacleCollides(obs, playerYAtObstacle, config.playerRadius)) {
      recordDeath(state, config, obs.beat, obs.id, events);
      return;
    }
    state.passedObstacleIds.add(obs.id);
    state.combo += 1;
    if (state.combo > state.maxCombo) {
      state.maxCombo = state.combo;
    }
    const verdict = gradeObstaclePass(obs, playerYAtObstacle, config.playerRadius);
    if (verdict === 'perfect') {
      state.perfects += 1;
    } else {
      state.goods += 1;
    }
    const baseScore = verdict === 'perfect' ? PERFECT_SCORE : GOOD_SCORE;
    const scoreDelta = Math.round(baseScore * comboMultiplier(state.combo));
    state.score += scoreDelta;
    const resolution: WavesResolution = {
      obstacleId: obs.id,
      beat: obs.beat,
      clearanceY: clearanceFor(obs, playerYAtObstacle),
      verdict,
    };
    state.resolutions.push(resolution);
    events.push({
      type: 'obstacle_passed',
      beat: obs.beat,
      payload: { obstacleId: obs.id, scoreDelta, combo: state.combo, verdict },
    });
    oi += 1;
  }
}

/**
 * Commit the player's Y position to the given beat, mutating state.
 * Called whenever the engine "locks in" the trajectory up to a beat
 * (input edges, tick boundaries, obstacle crossings). The piecewise-
 * linear segment anchored at `lastEdgeBeat` is replaced with one
 * anchored at `targetBeat`.
 */
function advancePlayerTo(
  state: WavesEngineState,
  config: WavesEngineConfig,
  targetBeat: number,
): void {
  const y = computePlayerYAt(state, config, targetBeat);
  state.lastEdgeBeat = targetBeat;
  state.lastEdgeY = y;
  state.playerY = y;
}

export function step(input: WavesStepInput): WavesStepOutput {
  const { config, fromBeat, toBeat } = input;
  const state = input.state;
  const events: WavesEngineEvent[] = [];

  if (!state.alive || state.completed) {
    return { state, events };
  }

  // Sort inputs in this window; the public API doesn't require pre-
  // sorted inputs, so we defensively sort here. Inputs outside
  // [fromBeat, toBeat] are silently dropped — callers stream them
  // by tick window, and the headless runner does too.
  const sortedInputs = [...input.inputs]
    .filter((i) => i.beat >= fromBeat && i.beat <= toBeat)
    .sort((a, b) => a.beat - b.beat);

  let cursor = Math.max(fromBeat, state.beat);

  for (const inp of sortedInputs) {
    // Evaluate hazards up to (but not including) the input edge — an
    // edge at exact same beat as an obstacle is reflected in the
    // playerY *at* that beat, but the engine resolves the hazard
    // using the trajectory before the edge so a player can't
    // "wave-cancel" at the obstacle beat to dodge it. (Same beat
    // resolution semantics as Pulse pads vs flipDown.)
    evaluateHazardsInRange(state, config, cursor, inp.beat, events);
    if (!state.alive) {
      return { state, events };
    }
    advancePlayerTo(state, config, inp.beat);
    const nextPressing = inp.type === 'down';
    if (nextPressing !== state.pressing) {
      state.pressing = nextPressing;
      events.push({
        type: 'edge',
        beat: inp.beat,
        payload: { pressing: state.pressing, playerY: state.playerY },
      });
    }
    cursor = inp.beat;
  }

  // Process any remaining hazards up to toBeat.
  evaluateHazardsInRange(state, config, cursor, toBeat, events);
  if (!state.alive) {
    return { state, events };
  }
  advancePlayerTo(state, config, toBeat);
  state.beat = toBeat;

  if (state.beat >= config.chart.totalBeats) {
    // Mark the loop complete once the playhead has crossed
    // `chart.totalBeats`. Obstacles exactly at totalBeats are already
    // resolved by the inclusive sweep above, so by the time we get
    // here every obstacle in the chart has had its judgement.
    state.completed = true;
    events.push({ type: 'completed', beat: state.beat });
  }

  return { state, events };
}

export function summariseWavesAccuracy(state: WavesEngineState): number {
  return computeAccuracy(state.perfects, state.goods, state.oks, state.misses);
}
